/**
 * Core authentication and initialization service for the application.
 * 
 * Primary responsibilities:
 * 1. MSAL Authentication
 *    - Configures and manages MSAL instance
 *    - Handles login/logout flows
 *    - Manages authentication state
 *    - Provides token acquisition for API calls
 * 
 * 2. User Management
 *    - Fetches user data on application start
 *    - Maintains user session state
 *    - Handles cross-tab authentication sync (optional)
 * 
 * Used by:
 * - main.ts for MSAL initialization
 * - app.module.ts for service providers and initialization
 * - Components/services requiring auth state or user data
 * 
 * Authentication Flow:
 * 1. MSAL initializes in main.ts
 * 2. CoreService initializes via APP_INITIALIZER
 * 3. User authentication state is established
 * 4. User data is fetched if authenticated - in app.component.ts
 */
import { Injectable } from '@angular/core';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
  LogLevel,
  IPublicClientApplication,
  PublicClientApplication,
  BrowserCacheLocation,
  InteractionType,
} from '@azure/msal-browser';
import { UserService } from './user.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class CoreService {
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  isAuthenticated$: Observable<boolean> =
    this.isAuthenticatedSubject.asObservable();
  private _destroying$ = new Subject<void>();

  constructor(
    private msalService: MsalService,
  ) {
    /* 
    // Optional: Auth state change listener for cross-tab communication
    // Uncomment if you need to handle auth state changes from other tabs
    // Without this isAuthenticated$ may not be accurate 
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => 
          msg.eventType === EventType.LOGIN_SUCCESS || 
          msg.eventType === EventType.LOGOUT_SUCCESS
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        const isLoggedIn = this.msalService.instance.getAllAccounts().length > 0;
        this.isAuthenticatedSubject.next(isLoggedIn);
      });
    */
  }

  /**
   * Initializes the service by setting the initial authentication state.
   * Called by APP_INITIALIZER after MSAL is initialized in main.ts
   */
  async init(): Promise<void> {
    try {
      console.log('CoreService initialization starting...');
      const accounts = this.msalService.instance.getAllAccounts();
        
      if (accounts.length > 0) {
        this.msalService.instance.setActiveAccount(accounts[0]);
        
        // Try to acquire a token silently to verify the session is still valid
        try {
          await this.msalService.instance.acquireTokenSilent({
            scopes: ['user.read'],
            account: accounts[0]
          });
          this.isAuthenticatedSubject.next(true);
        } catch (tokenError) {
          console.warn('Token acquisition failed, clearing cache and redirecting to login:', tokenError);
          // Clear browser storage
          this.msalService.instance.clearCache();
          localStorage.clear();
          sessionStorage.clear();
          
          // Redirect to login
          await this.msalService.loginRedirect();
        }
      } else {
        // No accounts found, initiate login
        console.log('No authenticated user found, initiating login...');
        await this.msalService.loginRedirect();
      }
  
      console.log('CoreService initialization complete');
    } catch (error) {
      console.error('Failed to initialize CoreService:', error);
      throw error;
    }
  }

  /**
   * Creates and configures the MSAL instance for the application.
   * Used by both main.ts for initialization and app.module.ts for DI.
   * 
   * @returns Configured MSAL public client application
   */
  static createMsalInstance(): IPublicClientApplication {
    return new PublicClientApplication({
      auth: {
        clientId: environment.clientId,
        authority: `https://login.microsoftonline.com/${environment.tenantId}`,
        redirectUri: window.location.origin,
        navigateToLoginRequestUrl: true,
        postLogoutRedirectUri: window.location.origin,
      },
      cache: {
        cacheLocation: BrowserCacheLocation.LocalStorage,
        storeAuthStateInCookie: this.isIE(),
      },
      system: {
        allowNativeBroker: false,
        loggerOptions: {
          logLevel: LogLevel.Error,
          loggerCallback: (level, message, containsPii) => {
            if (!containsPii) {
              console.log(`MSAL Log [${LogLevel[level]}]: ${message}`);
            }
          },
        },
      },
    });
  }

  /**
   * Provides configuration for the MSAL interceptor to protect API calls.
   * Maps resources to their required scopes for automatic token acquisition.
   * 
   * @returns MSAL interceptor configuration
   */
  fetchMsalInterceptorConfig() {
    const protectedResourceMap = new Map<string, Array<string>>();
    
    // Microsoft Graph scopes
    protectedResourceMap.set('https://graph.microsoft.com/v1.0/', [
      'user.read',
    ]);
    
    // Your API - use .default scope
    protectedResourceMap.set(environment.provisionerUrl, [
      `${environment.provisionerApiIdentifier}/.default`
    ]);
  
    const config = {
      interactionType: InteractionType.Redirect,
      protectedResourceMap,
    };
  
    console.log('MSAL Interceptor Config:', config);
    return config;
}

  /**
   * Provides configuration for the MSAL guard to protect routes.
   * Defines the interaction type and required scopes for route access.
   * 
   * @returns MSAL guard configuration
   */
  fetchMsalGuardConfig() {
    return {
      interactionType: InteractionType.Redirect,
      authRequest: {
        scopes: ['user.read', 'email', 'offline_access', 'openid', 'profile'],
      },
    };
  }

  /**
   * Detects if the current browser is Internet Explorer.
   * Used to determine if cookies are needed for token storage.
   * 
   * @returns True if browser is IE, false otherwise
   */
  private static isIE(): boolean {
    const ua = window.navigator.userAgent;
    const msie = ua.indexOf('MSIE ');
    const msie11 = ua.indexOf('Trident/');
    return msie > 0 || msie11 > 0;
  }

  /**
   * Acquires an access token silently for API calls.
   * Attempts to get token from cache first, then refreshes if needed.
   * 
   * @returns Promise resolving to access token or null if acquisition fails
   */
  async getToken(): Promise<string | null> {
    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length > 0) {
      try {
        const result = await this.msalService.instance.acquireTokenSilent({
          scopes: ['User.Read'],
          account: accounts[0],
        });
        return result.accessToken;
      } catch (error) {
        console.error('Token acquisition failed', error);
        return null;
      }
    }
    return null;
  }

  /**
   * Checks if a user is currently logged in by looking for cached accounts.
   * 
   * @returns True if user is logged in, false otherwise
   */
  isLoggedIn(): boolean {
    return this.msalService.instance.getAllAccounts().length > 0;
  }

  /**
   * Cleanup method called when service is destroyed.
   * Completes all observables to prevent memory leaks.
   */
  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }

  async simulateTokenExpiration(): Promise<void> {
    try {
      // Get all msal-related entries
      const msalKeys = Object.keys(localStorage).filter(key => 
        key.startsWith('msal.')
      );
  
      // Modify all token entries
      msalKeys.forEach(key => {
        try {
          // Skip non-token entries that might cause parsing errors
          if (key.includes('account.keys') || key.includes('active-account-filters')) {
            return;
          }
  
          const value = localStorage.getItem(key);
          if (!value) return;
  
          const entry = JSON.parse(value);
          
          if (key.includes('accesstoken') || key.includes('refreshtoken')) {
            entry.expiresOn = '1644040862';  // Past timestamp
            entry.extendedExpiresOn = '1644040862';
            localStorage.setItem(key, JSON.stringify(entry));
            console.log(`Modified token: ${key}`);
          }
        } catch (parseError) {
          // Skip entries that can't be parsed
          console.debug(`Skipping token ${key} - not a JSON value`);
        }
      });
  
      console.log('Tokens expired, clearing cache and redirecting...');
      
      // Clear all storage and redirect
      this.msalService.instance.clearCache();
      localStorage.clear();
      sessionStorage.clear();
      
      // Redirect to login without going through init()
      window.location.href = '/';
  
    } catch (error) {
      console.error('Error during token expiration simulation:', error);
      window.location.href = '/';
    }
  }
}
